/**
 * Sanitizes a string to be used as part of a test ID.
 * Converts spaces to hyphens, removes special characters (except hyphens and underscores),
 * and converts to lowercase.
 *
 * @param value - The string to sanitize
 * @returns A sanitized string safe for use in test IDs
 */
function sanitizeTestIdPart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-_]/g, ''); // Remove special characters except hyphens and underscores
}

/**
 * Generates a unique test ID by combining a base type with an identifier.
 * If no identifier is provided or if it's empty/whitespace-only, returns the base type unchanged.
 * The identifier is sanitized to handle special characters and spaces.
 *
 * @param type - The element type (e.g., 'input', 'select', 'checkbox')
 * @param identifier - Optional unique identifier (e.g., field name or ID). Will be sanitized.
 * @returns A test ID string in format: {type}-{identifier}
 *
 * @example
 * makeUniqueTestId('select', 'username') // 'select-username'
 * makeUniqueTestId('input', 'user name') // 'input-user-name'
 * makeUniqueTestId('checkbox', 'user@email') // 'checkbox-useremail'
 * makeUniqueTestId('select', undefined) // 'select'
 * makeUniqueTestId('input', '   ') // 'input'
 * makeUniqueTestId('select', '') // 'select'
 */
export function makeUniqueTestId(type: string, identifier?: string): string {
  if (!identifier || !identifier.trim()) {
    return type;
  }
  const sanitized = sanitizeTestIdPart(identifier);
  return sanitized ? `${type}-${sanitized}` : type;
}

/**
 * Generates a test ID for a modal field.
 * Format: modal-{feature}-{type}-{name}
 *
 * @param feature - The modal feature/context (e.g., 'add-product', 'delete-employer')
 * @param type - The field type (e.g., 'input', 'select', 'checkbox')
 * @param name - The field name
 * @returns A test ID string in format: modal-{feature}-{type}-{name}
 *
 * @example
 * makeModalFieldTestId('add-product', 'select', 'insurer') // 'modal-add-product-select-insurer'
 * makeModalFieldTestId('delete-employer', 'input', 'confirm-name') // 'modal-delete-employer-input-confirm-name'
 */
export function makeModalFieldTestId(feature: string, type: string, name?: string): string {
  const sanitizedFeature = sanitizeTestIdPart(feature);
  const sanitizedType = sanitizeTestIdPart(type);

  if (!name || !name.trim()) {
    return `modal-${sanitizedFeature}-${sanitizedType}`;
  }

  const sanitizedName = sanitizeTestIdPart(name);
  return `modal-${sanitizedFeature}-${sanitizedType}-${sanitizedName}`;
}

/**
 * Generates a test ID for a button.
 * Format: {action}-{feature}-button
 *
 * @param action - The button action (e.g., 'add', 'delete', 'edit')
 * @param feature - The feature context (e.g., 'product', 'employer', 'user')
 * @returns A test ID string in format: {action}-{feature}-button
 *
 * @example
 * makeButtonTestId('add', 'product') // 'add-product-button'
 * makeButtonTestId('delete', 'employer') // 'delete-employer-button'
 * makeButtonTestId('edit', 'user profile') // 'edit-user-profile-button'
 */
export function makeButtonTestId(action: string, feature: string): string {
  const sanitizedAction = sanitizeTestIdPart(action);
  const sanitizedFeature = sanitizeTestIdPart(feature);
  return `${sanitizedAction}-${sanitizedFeature}-button`;
}

/**
 * Generates a test ID for a cancel button.
 * Format: cancel-{feature}-button
 *
 * @param feature - The modal/form feature. If undefined, returns undefined.
 * @returns A test ID string in format: cancel-{feature}-button, or undefined if feature is undefined
 *
 * @example
 * makeButtonCancelTestId('add-product') // 'cancel-add-product-button'
 * makeButtonCancelTestId('delete-employer') // 'cancel-delete-employer-button'
 * makeButtonCancelTestId(undefined) // undefined
 */
export function makeButtonCancelTestId(feature?: string): string | undefined {
  if (!feature) return undefined;
  const sanitized = sanitizeTestIdPart(feature);
  return `cancel-${sanitized}-button`;
}

/**
 * Generates a test ID for a confirm button.
 * Format: confirm-{feature}-button
 *
 * @param feature - The modal/form feature. If undefined, returns undefined.
 * @returns A test ID string in format: confirm-{feature}-button, or undefined if feature is undefined
 *
 * @example
 * makeButtonConfirmTestId('add-product') // 'confirm-add-product-button'
 * makeButtonConfirmTestId('delete-employer') // 'confirm-delete-employer-button'
 * makeButtonConfirmTestId(undefined) // undefined
 */
export function makeButtonConfirmTestId(feature?: string): string | undefined {
  if (!feature) return undefined;
  const sanitized = sanitizeTestIdPart(feature);
  return `confirm-${sanitized}-button`;
}

/**
 * Generates a test ID for a submit button.
 * Format: submit-{feature}-button
 *
 * @param feature - The modal/form feature. If undefined, returns undefined.
 * @returns A test ID string in format: submit-{feature}-button, or undefined if feature is undefined
 *
 * @example
 * makeButtonSubmitTestId('add-product') // 'submit-add-product-button'
 * makeButtonSubmitTestId('create-user') // 'submit-create-user-button'
 * makeButtonSubmitTestId(undefined) // undefined
 */
export function makeButtonSubmitTestId(feature?: string): string | undefined {
  if (!feature) return undefined;
  const sanitized = sanitizeTestIdPart(feature);
  return `submit-${sanitized}-button`;
}
